import type { ShotResult } from '../shared/types';
import { attackActionType, attackTiming, ORIGINAL_ACTION_TICK_MS } from '../shared/attack-timing';
import { sourceFrameBlend } from './source-frame-blend';

export type CharacterAction = 'idle' | 'walk' | 'shot' | 'throw' | 'showgun' | 'showthrows' | 'hidegun' | 'hidethrows' | 'handclip' | 'hit' | 'cry';
export interface CharacterAnimationAsset {
  idle: string | string[]; walk: string[]; shot: string[];
  throw?: string[]; showgun?: string[]; showthrows?: string[];
  hidegun?: string[]; hidethrows?: string[];
  handclip?: string[]; hit?: string[]; cry?: string[];
  frameRate?: number;
  animationTimelines?: Partial<Record<CharacterAction, { frameRate?: number; sourceFrames?: number[] }>>;
}
export interface CharacterFrame {
  action: CharacterAction; path: string; frameIndex: number; sourceFrame: number;
  nextPath: string; nextFrameIndex: number; nextSourceFrame: number; mix: number;
  startedAt: number; key: string;
  weaponPhase: 'start' | 'shot' | 'end' | null;
  weaponPhaseStartedAt: number | null; weaponPhaseElapsed: number;
  weaponId: string | null; kind: ShotResult['kind'] | null;
  bodyEntryDelayMs: number;
}
type WeaponEndDuration = number | ((weaponId: string, kind: ShotResult['kind']) => number);
interface AnimationClock { key: string; startedAt: number }
interface Preparation { weaponId: string; kind: ShotResult['kind']; startedAt: number }
interface ShotPreview extends Preparation { confirmedShotId: string | null }
interface FeedbackAction { action: 'handclip' | 'hit' | 'cry'; startedAt: number }

export function shotActionType(weaponId: string, kind: ShotResult['kind']): 0 | 1 {
  return attackActionType(weaponId, kind);
}

/** Same-time native salvo rays; retain grouping for old persisted 90ms pellets. */
export function volleyLaunches(shot: Pick<ShotResult, 'trajectories'>): number[] {
  const delays = [...new Set(shot.trajectories.map((projectile) => projectile.launchDelay))].sort((a, b) => a - b);
  return delays.filter((delay, index) => !index || delay - delays[index - 1] > 180);
}

/** Independent WeaponMovie END may continue after the one-tick body HIDE. */
export function attackPresentationEnd(shot: ShotResult, weaponEndMs = 0): number {
  const launches = volleyLaunches(shot), timing = attackTiming(shot.weaponId, shot.kind);
  if (!launches.length) return 0;
  const modern = launches[0] >= timing.windupMs;
  return launches.at(-1)! + (modern ? 0 : timing.attackFrameMs) + timing.holdMs + Math.max(timing.hideMs, weaponEndMs);
}

function framesFor(character: CharacterAnimationAsset, action: CharacterAction): string[] {
  if (action === 'idle') return Array.isArray(character.idle) ? character.idle : [character.idle];
  if (action === 'hidegun') return character.hidegun?.length ? character.hidegun : [character.shot.at(-1)!];
  if (action === 'hidethrows') return character.hidethrows?.length ? character.hidethrows : [character.throw?.at(-1) ?? character.shot.at(-1)!];
  return character[action]?.length ? character[action]! : Array.isArray(character.idle) ? character.idle : [character.idle];
}
function rateFor(character: CharacterAnimationAsset, action: CharacterAction): number {
  const rate = character.animationTimelines?.[action]?.frameRate ?? character.frameRate ?? 25;
  return Number.isFinite(rate) && rate > 0 ? rate : 25;
}
function frame(character: CharacterAnimationAsset, action: CharacterAction, key: string, startedAt: number, now: number, loop: boolean): CharacterFrame {
  const frames = framesFor(character, action);
  const { frameIndex, nextFrameIndex, mix } = sourceFrameBlend(now - startedAt, rateFor(character, action), frames.length, loop);
  const path = frames[frameIndex];
  const nextPath = frames[nextFrameIndex];
  return { action, key, startedAt, path, frameIndex, sourceFrame: character.animationTimelines?.[action]?.sourceFrames?.[frameIndex] ?? Number(path.match(/frame-(\d+)\.png(?:$|\?)/)?.[1] ?? 0),
    nextPath, nextFrameIndex, mix, nextSourceFrame: character.animationTimelines?.[action]?.sourceFrames?.[nextFrameIndex] ?? Number(nextPath.match(/frame-(\d+)\.png(?:$|\?)/)?.[1] ?? 0),
    weaponPhase: null, weaponPhaseStartedAt: null, weaponPhaseElapsed: 0, weaponId: null, kind: null, bodyEntryDelayMs: 0 };
}

/** Original actions use their own 25 fps clock, never global wall-clock phase. */
export class CharacterAnimation {
  private baseClocks = new Map<string, AnimationClock>();
  private preparation = new Map<string, Preparation>();
  private previews = new Map<string, ShotPreview>();
  private confirmedFirstVolleys = new Set<string>();
  private feedback = new Map<string, FeedbackAction>();
  private interruptedCycles = new Set<string>();
  private presentedBodyFrames = new Map<string, CharacterFrame>();
  private bodyEntries = new Map<string, { key: string; from: CharacterFrame }>();

  useItem(playerId: string, now = performance.now()): void {
    // HANDCLIP.replaceSame=true: repeated accepted props restart this action.
    this.feedback.set(playerId, { action: 'handclip', startedAt: now });
  }
  hurt(playerId: string, now = performance.now(), action: 'hit' | 'cry' = 'cry'): void {
    const current = this.feedback.get(playerId);
    // PlayerAction.canReplace ignores another active CRY/HIT of the same type.
    const duration = current?.action === 'handclip' ? 2000 : current?.action === 'hit' ? 800 : 1200;
    if (current?.action === action && now - current.startedAt < duration) return;
    this.feedback.set(playerId, { action, startedAt: now });
  }
  cancelFeedback(playerId: string): void { this.feedback.delete(playerId); }

  prepare(playerId: string, weaponId: string, kind: ShotResult['kind'], now = performance.now()): void {
    this.preparation.set(playerId, { weaponId, kind, startedAt: now });
  }
  cancelPreparation(playerId?: string): void {
    if (playerId) this.preparation.delete(playerId); else this.preparation.clear();
  }
  preview(playerId: string, weaponId: string, kind: ShotResult['kind'], now = performance.now()): void {
    this.cancelPreparation(playerId);
    this.previews.set(playerId, { weaponId, kind, startedAt: now, confirmedShotId: null });
  }
  confirmShot(shot: ShotResult, now = performance.now()): number | undefined {
    const preview = this.previews.get(shot.playerId);
    if (preview && (!preview.confirmedShotId || preview.confirmedShotId === shot.id)
      && preview.weaponId === shot.weaponId && preview.kind === shot.kind && now - preview.startedAt < 3000) {
      preview.confirmedShotId = shot.id;
      // ACK binds the shot identity; the visible first action keeps the
      // release clock. Rebasing it and clamping elapsed would freeze frames
      // until the new clock catches up with the frame already on screen.
      this.confirmedFirstVolleys.add(shot.id);
      this.cancelPreparation(shot.playerId);
      return preview.startedAt;
    }
    this.cancelPreparation(shot.playerId);
    return undefined;
  }
  cancelPreview(playerId?: string): void {
    if (playerId) this.previews.delete(playerId); else this.previews.clear();
  }
  get awaitingShot() {
    const now = performance.now();
    for (const [playerId, preview] of this.previews) if (!preview.confirmedShotId && now - preview.startedAt >= 3000) this.previews.delete(playerId);
    return [...this.previews.values()].some((preview) => !preview.confirmedShotId && now - preview.startedAt < 3000);
  }

  previewStartedAt(playerId: string): number | undefined { return this.previews.get(playerId)?.startedAt; }
  pendingKind(playerId: string): ShotResult['kind'] | undefined { return this.previews.get(playerId)?.kind ?? this.preparation.get(playerId)?.kind; }

  sample(character: CharacterAnimationAsset, playerId: string, now: number, walking: boolean, shot?: { result: ShotResult; startedAt: number }, weaponEndMs: WeaponEndDuration = 0): CharacterFrame {
    let sampled = this.sampleAction(character, playerId, now, walking, shot, weaponEndMs);
    if (sampled.bodyEntryDelayMs > 0 && (sampled.weaponPhase === 'start' || sampled.weaponPhase === 'shot')) {
      const age = now - sampled.startedAt;
      let entry = this.bodyEntries.get(playerId);
      if (entry?.key !== sampled.key) {
        // Native doAction changes the action/index, but does not repaint the
        // body until its next ENTER_FRAME. Resample that first source tick
        // from the pose already drawn, instead of popping to the raised arm.
        const previous = this.presentedBodyFrames.get(playerId);
        const timing = attackTiming(sampled.weaponId!, sampled.kind!);
        const from = previous ?? (sampled.weaponPhase === 'shot'
          ? frame(character, timing.prepare, `${sampled.key}:previous`, now - timing.prepareFrameMs, now, false)
          : frame(character, 'idle', 'idle', now, now, true));
        entry = { key: sampled.key, from }; this.bodyEntries.set(playerId, entry);
      }
      if (age >= 0 && age < sampled.bodyEntryDelayMs) {
        sampled = { ...sampled, path: entry.from.path, frameIndex: entry.from.frameIndex, sourceFrame: entry.from.sourceFrame,
          nextPath: sampled.path, nextFrameIndex: sampled.frameIndex, nextSourceFrame: sampled.sourceFrame, mix: age / sampled.bodyEntryDelayMs };
      }
    } else this.bodyEntries.delete(playerId);
    this.presentedBodyFrames.set(playerId, sampled);
    return sampled;
  }

  private sampleAction(character: CharacterAnimationAsset, playerId: string, now: number, walking: boolean, shot?: { result: ShotResult; startedAt: number }, weaponEndMs: WeaponEndDuration = 0): CharacterFrame {
    const endDuration = (weaponId: string, kind: ShotResult['kind']) => typeof weaponEndMs === 'number' ? weaponEndMs : weaponEndMs(weaponId, kind);
    const actionFrame = (action: CharacterAction, key: string, startedAt: number, sampleNow = now, bodyDelay = 0) => {
      // GameCharacter.doAction resets its frame index when an action ends.
      // Returning to STAND must start its own timeline again as well.
      this.baseClocks.delete(playerId);
      // SHOW/SHOT body rendering is driven by the first source update after
      // doAction; WeaponMovie's gotoAndPlay remains immediate and independent.
      return frame(character, action, key, startedAt, sampleNow - bodyDelay, false);
    };
    const preview = this.previews.get(playerId);
    const cycle = (weaponId: string, kind: ShotResult['kind'], launch: number, startsAt: number, key: string, sampleNow: number, modern: boolean): CharacterFrame | null => {
      const nativeTiming = attackTiming(weaponId, kind);
      // Persisted immediate-emission packets predate the source action queue.
      // Keep their old body clock; only current timed packets use its barriers.
      const timing = modern ? nativeTiming : { ...nativeTiming, prepareMs: nativeTiming.prepareFrameMs, attackMs: nativeTiming.attackFrameMs };
      const bodyDelay = modern ? ORIGINAL_ACTION_TICK_MS : 0;
      const elapsed = sampleNow - startsAt;
      const prepareAt = modern ? launch - timing.windupMs : launch - timing.prepareMs;
      const attackAt = modern ? launch - timing.attackMs : launch;
      const emittedAt = modern ? launch : launch + timing.attackMs;
      const hideAt = emittedAt + timing.holdMs;
      const withWeapon = (body: CharacterFrame, phase: 'start' | 'shot' | 'end', phaseAt: number): CharacterFrame => ({ ...body,
        weaponId, kind, weaponPhase: phase, weaponPhaseStartedAt: startsAt + phaseAt, weaponPhaseElapsed: Math.max(0, elapsed - phaseAt),
        bodyEntryDelayMs: phase === 'start' || phase === 'shot' ? bodyDelay : 0 });
      if (elapsed < prepareAt || elapsed >= hideAt + Math.max(timing.hideMs, endDuration(weaponId, kind))) return null;
      const feedback = this.feedback.get(playerId);
      const phaseAt = elapsed < attackAt ? prepareAt : attackAt;
      // Native distinct actions replace each other. A new SHOW/SHOT replaces
      // an older prop/cry; a later cry interrupts SHOT and hides WeaponMovie.
      // ShootBombAction does not issue HIDE after such an interruption, so
      // neither the old attack pose nor its weapon may resume afterwards.
      if (feedback && feedback.startedAt <= startsAt + phaseAt) this.feedback.delete(playerId);
      else if (feedback && feedback.startedAt <= sampleNow) {
        if (elapsed >= attackAt) this.interruptedCycles.add(key);
        return null;
      }
      if (this.interruptedCycles.has(key)) return null;
      if (elapsed < attackAt) return withWeapon(actionFrame(timing.prepare, `${key}:${timing.prepare}`, startsAt + prepareAt, sampleNow, bodyDelay), 'start', prepareAt);
      // SHOT and THROWS have stopAtEnd=true. Clamp their final source frame
      // for the original shoot interval instead of returning to STAND early.
      if (elapsed < hideAt) return withWeapon(actionFrame(timing.attack, `${key}:${timing.attack}`, startsAt + attackAt, sampleNow, bodyDelay), 'shot', attackAt);
      if (elapsed < hideAt + timing.hideMs) return withWeapon(actionFrame(timing.hide, `${key}:${timing.hide}`, startsAt + hideAt, sampleNow), 'end', hideAt);
      // WeaponMovie's native end label can outlast the one-frame body HIDE.
      // Restore STAND on its original clock while that independent layer ends.
      let idle = this.baseClocks.get(playerId);
      if (idle?.key !== 'idle') { idle = { key: 'idle', startedAt: startsAt + hideAt + timing.hideMs }; this.baseClocks.set(playerId, idle); }
      return withWeapon(frame(character, 'idle', idle.key, idle.startedAt, sampleNow, true), 'end', hideAt);
    };
    const samplePreview = (): CharacterFrame | null => {
      if (!preview) return null;
      const timing = attackTiming(preview.weaponId, preview.kind);
      const startsAt = preview.startedAt;
      const sampled = cycle(preview.weaponId, preview.kind, timing.windupMs, startsAt, `preview:${preview.startedAt}`, now, true);
      const completeMs = timing.windupMs + timing.holdMs + Math.max(timing.hideMs, endDuration(preview.weaponId, preview.kind));
      if (!sampled && now - startsAt >= completeMs && (preview.confirmedShotId || now - preview.startedAt >= 3000)) this.previews.delete(playerId);
      return sampled;
    };
    // A new local release can overlap the preceding independent END layer.
    // Its SHOW replaces that old cycle immediately, before authority arrives.
    if (preview && !preview.confirmedShotId) {
      const sampled = samplePreview();
      if (sampled) return sampled;
    }
    if (shot?.result.playerId === playerId) {
      const elapsed = now - shot.startedAt;
      const launches = volleyLaunches(shot.result);
      const timing = attackTiming(shot.result.weaponId, shot.result.kind);
      const modern = (launches[0] ?? 0) >= timing.windupMs;
      let current = -1;
      for (let index = 0; index < launches.length; index++) {
        const prepareAt = modern ? launches[index] - timing.windupMs : launches[index] - timing.prepareMs;
        if (prepareAt <= elapsed) current = index;
      }
      // Retiring a completed preview must not expose the authority's first
      // cycle again, including a late ACK after the local HIDE/END completed.
      if (current >= 0 && !(current === 0 && this.confirmedFirstVolleys.has(shot.result.id))) {
        const sampled = cycle(shot.result.weaponId, shot.result.kind, launches[current], shot.startedAt, `${shot.result.id}:${current}`, now, modern);
        if (sampled) return sampled;
      }
    }
    if (preview) {
      const sampled = samplePreview();
      if (sampled) return sampled;
    }
    const preparation = this.preparation.get(playerId);
    if (preparation) {
      const action: CharacterAction = shotActionType(preparation.weaponId, preparation.kind) === 0 ? 'showthrows' : 'showgun';
      return actionFrame(action, `charge:${preparation.startedAt}:${action}`, preparation.startedAt);
    }
    const feedback = this.feedback.get(playerId);
    if (feedback) {
      const frames = character[feedback.action];
      const duration = (frames?.length ?? 0) * 1000 / rateFor(character, feedback.action);
      // Native HANDCLIP refuses replacement by WALK. CRY/HIT are replaceable
      // by a distinct movement action, matching PlayerAction.canReplace.
      if (now >= feedback.startedAt && now - feedback.startedAt < duration && (!walking || feedback.action === 'handclip')) {
        return actionFrame(feedback.action, `feedback:${feedback.startedAt}:${feedback.action}`, feedback.startedAt);
      }
      if (now >= feedback.startedAt + duration || walking) this.feedback.delete(playerId);
    }
    const action: CharacterAction = walking && character.walk.length ? 'walk' : 'idle';
    let clock = this.baseClocks.get(playerId);
    if (clock?.key !== action) { clock = { key: action, startedAt: now }; this.baseClocks.set(playerId, clock); }
    return frame(character, action, clock!.key, clock!.startedAt, now, true);
  }

  reset(): void { this.baseClocks.clear(); this.preparation.clear(); this.previews.clear(); this.confirmedFirstVolleys.clear(); this.feedback.clear(); this.interruptedCycles.clear(); this.presentedBodyFrames.clear(); this.bodyEntries.clear(); }
}
